import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../../api/adminApi";
import type { LookupObjectReference } from "../../../../../../api/types/objectTypes";
import type { WorkflowActivity } from "../../../../../../api/types/workflowTypes";

interface Props {
  workflowId: string;
  activityId: string;
  objectTypeOptions: string[];
  onClose: () => void;
  onSaved: (updatedActivity: WorkflowActivity) => void;
}

const createWorkflowActivityChangeSet = withErrorHandling(adminApi.createWorkflowActivityChangeSet);

const ActivityChangeSetCreationDialog = ({ workflowId, activityId, objectTypeOptions, onClose, onSaved }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [selectedObjectType, setSelectedObjectType] = useState(
    objectTypeOptions.length === 1 ? objectTypeOptions[0] : undefined
  );

  const [selectedObject, setSelectedObject] = useState<LookupObjectReference | null>(null);

  const [saving, setSaving] = useState(false);

  const getObjects = useCallback(
    () => adminApi.getObjectsAsLookupReferences(defined(selectedObjectType)),
    [selectedObjectType]
  );

  const [objects, fetchError, { isFetching }] = useFetch(getObjects, { disabled: !selectedObjectType });

  const handleSave = async () => {
    if (!selectedObjectType || !selectedObject) {
      return;
    }

    setSaving(true);
    const [updatedActivity, error] = await createWorkflowActivityChangeSet(workflowId, activityId, {
      objectInfo: { objectType: selectedObjectType, objectId: selectedObject.objectId },
    });
    setSaving(false);

    if (error) {
      logError(error, "[ActivityChangeSetCreationDialog] createWorkflowActivityChangeSet");
      sendNotificationError(error.message);
      return;
    }

    onSaved(updatedActivity);
  };

  const handleChangeObjectType = (objectType: string) => {
    setSelectedObjectType(objectType);
    setSelectedObject(null);
  };

  const isValid = !!selectedObjectType && !!selectedObject;

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>Edit Activity</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <DialogContent>
        <Stack spacing={3}>
          <Typography color="textSecondary">Select an object that will be applied to this activity</Typography>

          <FormControl fullWidth>
            <InputLabel>Object Type</InputLabel>
            <Select
              label="Object Type"
              value={selectedObjectType}
              onChange={(e) => handleChangeObjectType(e.target.value)}
              disabled={objectTypeOptions.length < 2}
            >
              {objectTypeOptions.map((objectType) => (
                <MenuItem key={objectType} value={objectType}>
                  {autoFormatCamelCase(objectType)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {fetchError && <DataLoadingFailed title="Failed to fetch objects" />}

          <Autocomplete<LookupObjectReference>
            fullWidth
            openOnFocus
            disabled={isFetching || !objects?.length}
            options={objects ?? []}
            value={selectedObject}
            onChange={(_, newValue) => setSelectedObject(newValue)}
            getOptionLabel={(o) =>
              o.secondaryFieldValue ? `${o.primaryFieldValue} (${o.secondaryFieldValue})` : o.primaryFieldValue
            }
            isOptionEqualToValue={(option, value) => option.objectId === value.objectId}
            renderInput={(params) => (
              <TextField
                {...params}
                label={isFetching ? "Loading..." : selectedObjectType ? autoFormatCamelCase(selectedObjectType) : ""}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button color="primary" variant="contained" onClick={handleSave} loading={saving} disabled={!isValid}>
          Save
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default ActivityChangeSetCreationDialog;
